import { useLocation } from 'react-router-dom';

export type Locale = 'en' | 'ml';

const ML_PREFIX = '/ml';

export type WorkContentOverride = Partial<{
  clientNameFull: string;
  location: string;
  category: string;
  homePageContent: string;
  footerContent: string;
  specsContent: string[];
}>;

export type LocalizedContent = {
  nav: {
    home: string;
    portfolio: string;
    contact: string;
  };
  footer: {
    home: string;
    works: string;
    steelWindowFactoryErp: string;
    enterpriseEcommerce: string;
    preOwnedCarsErp: string;
    followUs: string;
    callUs: string;
    address: string;
    techDrivenBy: string;
  };
  cta: {
    scheduleButtonText: string;
    whatsAppButtonText: string;
    scheduleHelperText: string;
    whatsAppHelperText: string;
  };
  homeHero: {
    eyebrow: string;
    webTitle: [string, string];
    mobileTitle: [string, string, string];
    descriptionLine1: string;
    descriptionLine2: string;
    highlight: string;
  };
  landingMeta: {
    title: string;
    content: string;
    description: string;
  };
  featuredCaseStudies: {
    headingLead: string;
    headingMiddle: string;
    headingAfter: string;
    headingTech: string;
    headingEnd: string;
    label: string;
    buttonText: string;
  };
  workOverrides: Record<string, WorkContentOverride>;
};

const enContent: LocalizedContent = {
  nav: {
    home: 'HOME',
    portfolio: 'PORTFOLIO',
    contact: 'CONTACT US',
  },
  footer: {
    home: 'Home',
    works: 'Works',
    steelWindowFactoryErp: 'Steel window factory ERP',
    enterpriseEcommerce: 'Enterprise e-commerce',
    preOwnedCarsErp: 'ERP for Pre-owned Cars',
    followUs: 'Follow us',
    callUs: 'Call us',
    address: 'Address',
    techDrivenBy: 'Tech Driven By',
  },
  cta: {
    scheduleButtonText: 'Schedule a Project Discussion',
    whatsAppButtonText: 'Chat On WhatsApp',
    scheduleHelperText:
      "Pick a time that suits you. We'll discuss your business, not push a sales pitch.",
    whatsAppHelperText: 'Have a quick question? Our team will reply on WhatsApp.',
  },
  homeHero: {
    eyebrow: 'We develop enterprise',
    webTitle: ['Custom Software', '& Systems'],
    mobileTitle: ['Custom', 'Software &', 'Systems'],
    descriptionLine1: 'that optimize your businesses',
    descriptionLine2: 'and make it truly',
    highlight: 'tech‑driven.',
  },
  landingMeta: {
    title: 'Tech-Driven Business Systems, Software & Apps | Osperb',
    content:
      'Osperb helps businesses systemise & go tech-driven with AI, ERP & e-commerce integrations, Custom softwares, Apps, & continuous optimization.',
    description:
      'Osperb helps businesses systemise & go tech-driven with AI, ERP & e-commerce integrations, Custom softwares, Apps, & continuous optimization.',
  },
  featuredCaseStudies: {
    headingLead: 'See the impact',
    headingMiddle: "on our clients' business",
    headingAfter: 'after going',
    headingTech: 'Tech-driven',
    headingEnd: 'with us',
    label: 'Featured case studies↓',
    buttonText: 'View case study',
  },
  workOverrides: {},
};

const mlContent: LocalizedContent = {
  ...enContent,
  nav: {
    home: 'HOME',
    portfolio: 'PORTFOLIO',
    contact: 'CONTACT US',
  },
  footer: {
    home: 'Home',
    works: 'Works',
    steelWindowFactoryErp: 'Steel window factory ERP',
    enterpriseEcommerce: 'Enterprise e-commerce',
    preOwnedCarsErp: 'ERP for Pre-owned Cars',
    followUs: 'Follow us',
    callUs: 'Call us',
    address: 'Address',
    techDrivenBy: 'Tech Driven By',
  },
  cta: {
    scheduleButtonText: 'Schedule a Project Discussion',
    whatsAppButtonText: 'Chat On WhatsApp',
    scheduleHelperText:
      "Pick a time that suits you. We'll discuss your business, not push a sales pitch.",
    whatsAppHelperText: 'Have a quick question? Our team will reply on WhatsApp.',
  },
  homeHero: {
    eyebrow: 'We develop enterprise',
    webTitle: ['Custom Software', '& Systems'],
    mobileTitle: ['Custom', 'Software &', 'Systems'],
    descriptionLine1: 'that optimize your businesses',
    descriptionLine2: 'and make it truly',
    highlight: 'tech‑driven.',
  },
  landingMeta: {
    title: 'Tech-Driven Business Systems, Software & Apps | Osperb',
    content:
      'Osperb helps businesses systemise & go tech-driven with AI, ERP & e-commerce integrations, Custom softwares, Apps, & continuous optimization.',
    description:
      'Osperb helps businesses systemise & go tech-driven with AI, ERP & e-commerce integrations, Custom softwares, Apps, & continuous optimization.',
  },
  featuredCaseStudies: {
    headingLead: 'See the impact',
    headingMiddle: "on our clients' business",
    headingAfter: 'after going',
    headingTech: 'Tech-driven',
    headingEnd: 'with us',
    label: 'Featured case studies↓',
    buttonText: 'View case study',
  },
  workOverrides: {},
};

const contentByLocale: Record<Locale, LocalizedContent> = {
  en: enContent,
  ml: mlContent,
};

export function isMalayalamPath(pathname: string): boolean {
  return pathname === ML_PREFIX || pathname.startsWith(`${ML_PREFIX}/`);
}

export function getLocaleFromPath(pathname: string): Locale {
  return isMalayalamPath(pathname) ? 'ml' : 'en';
}

export function stripLocalePrefix(pathname: string): string {
  if (!pathname) return '/';
  if (pathname === ML_PREFIX) return '/';
  if (pathname.startsWith(`${ML_PREFIX}/`)) return pathname.slice(ML_PREFIX.length) || '/';
  return pathname;
}

export function getLocalizedPath(to: string, locale: Locale): string {
  if (!to || /^(https?:|mailto:|tel:|sms:|#|\/\/)/.test(to)) return to;
  if (!to.startsWith('/')) return to;

  const hashIndex = to.indexOf('#');
  const beforeHash = hashIndex >= 0 ? to.slice(0, hashIndex) : to;
  const hash = hashIndex >= 0 ? to.slice(hashIndex) : '';
  const queryIndex = beforeHash.indexOf('?');
  const pathname = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex) : '';

  const basePath = stripLocalePrefix(pathname);
  const localizedPath =
    locale === 'ml' ? (basePath === '/' ? ML_PREFIX : `${ML_PREFIX}${basePath}`) : basePath;

  return `${localizedPath}${query}${hash}`;
}

export function getContent(locale: Locale): LocalizedContent {
  return contentByLocale[locale] || enContent;
}

export function useLocale(): Locale {
  const location = useLocation();
  return getLocaleFromPath(location.pathname);
}

export function localizeWorkData<T extends { id: string | number }>(
  projects: T[],
  locale: Locale
): T[] {
  const overrides = getContent(locale).workOverrides;

  return projects.map((project) => ({
    ...project,
    ...(overrides[String(project.id)] || {}),
  })) as T[];
}
